import { useCallback, useId, useRef } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import useControllableState from 'helpers/hooks/useControllableState';

export interface CheckboxProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'key'> {
  label?: React.ReactNode;
  labelPosition?: 'on-left' | 'on-right';
  containerClassName?: string;
  onChange?: (props: { name: string; checked: boolean }) => void;
  disableBackground?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({
  className = '',
  checked,
  onChange,
  containerClassName,
  label,
  defaultChecked = false,
  labelPosition = 'on-right',
  disableBackground = false,
  required,
  id,
  ...props
}) => {
  const generatedId = useId();
  id = id || generatedId;
  const labelId = useId();

  const [isChecked, setIsChecked] = useControllableState(checked, defaultChecked);

  const checkboxRef = useRef<HTMLInputElement>(null);

  const toggleIsChecked = useCallback(() => {
    onChange?.({ name: props.name ?? '', checked: !isChecked });

    setIsChecked(!isChecked);
  }, [isChecked, onChange, props.name, setIsChecked]);

  const containerClassNames = classNames('flex items-center gap-3 group', containerClassName);
  const buttonClassName = classNames(
    'h-5 w-5 min-w-5 rounded-sm relative outline outline-1 cursor-pointer',
    'outline-neutral-400 lg:group-hover:outline-secondary-black',
  );

  const inputClassName = classNames(
    'absolute w-full h-full z-20 checked:bg-none bg-transparent text-transparent border-transparent',
    className,
  );

  const iconClassName = classNames(
    'absolute top-[50%] z-10 left-[50%] h-4 w-4 translate-y-[-50%] translate-x-[-50%] stroke-[2px] lg:group-hover:block',
    isChecked ? 'block' : 'hidden',
    isChecked && !disableBackground ? 'text-white' : 'text-secondary-black',
  );

  const backgroundClassName = classNames('absolute z-0 h-5 w-5 rounded-sm', {
    'bg-secondary-black': !!isChecked,
  });

  const LabelElement = (
    <label id={labelId} htmlFor={id} className="grow text-[14px] text-secondary-black">
      {label} {required ? ' *' : ''}
    </label>
  );

  return (
    <div className={containerClassNames}>
      {label && labelPosition === 'on-left' && LabelElement}

      <div className={buttonClassName}>
        <input
          ref={checkboxRef}
          type="checkbox"
          aria-labelledby={labelId}
          checked={isChecked}
          className={inputClassName}
          onChange={toggleIsChecked}
          id={id}
          {...props}
        />

        <CheckIcon className={iconClassName} />

        {!disableBackground && <div className={backgroundClassName} />}
      </div>

      {label && labelPosition === 'on-right' && LabelElement}
    </div>
  );
};

export default Checkbox;
